import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Rating from '@mui/material/Rating';
import EbayPriceValue from '../EbayPriceValue';
import { getSecureImageUrl, handleImageError, getConsoleFallbackImageUrl, getConsoleImageUrl } from '../../utils/imageUtils';

/**
 * Fixed detailed view component for collection/wishlist items with proper edit button functionality
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of items to display
 * @param {function} props.onEdit - Function to handle edit button click
 * @param {function} props.onDelete - Function to handle delete button click
 * @param {string} props.itemType - Type of item ('game', 'console', 'lego')
 * @param {boolean} props.isWishlist - Whether this is a wishlist view
 * @param {function} props.onMoveToCollection - Function to handle moving item to collection (for wishlists)
 * @param {function} props.onUpdateItems - Function to update items after price update
 * @param {Object} props.renderProps - Custom render props for item-specific fields
 * @returns {JSX.Element} Detailed view component
 */
const FixedDetailedView = ({
  items,
  onEdit,
  onDelete,
  itemType = 'game',
  isWishlist = false,
  onMoveToCollection,
  onToggleFavourite,
  onUpdateItems,
  renderProps = {}
}) => {
  const navigate = useNavigate();

  // Default render functions
  const defaultRenderProps = {
    getImageUrl: (item) => {
      if (itemType === 'console') {
        // Use the specialized console image utility function
        return getConsoleImageUrl(item);
      }
      return getSecureImageUrl(item.imageUrl || item.set_img_url);
    },
    getItemName: (item) => item.name,
    getItemPrimary: (item) => {
      if (itemType === 'game') return `Platform: ${item.platform}`;
      if (itemType === 'console') return `Generation: ${item.generation || 'N/A'}`;
      if (itemType === 'lego') {
        const setNum = item.setNum || item.set_num;
        return `Set #: ${setNum ? setNum.split('-')[0] : 'N/A'}`;
      }
      if (itemType === 'movie') return `Format: ${item.format || 'N/A'}`;
      if (itemType === 'music') return `Artist: ${item.artist || 'N/A'}`;
      if (itemType === 'book') return `Author: ${item.author || 'N/A'}`;
      if (itemType === 'board-game') {
        const players = item.minPlayers && item.maxPlayers 
          ? (item.minPlayers === item.maxPlayers ? `${item.minPlayers} players` : `${item.minPlayers}-${item.maxPlayers} players`)
          : 'N/A';
        return `Players: ${players}`;
      }
      return '';
    },
    getItemSecondary: (item) => {
      if (itemType === 'game') return `Priority: ${item.priority || 'Medium'}`;
      if (itemType === 'console') return `Condition: ${item.condition || 'N/A'}`;
      if (itemType === 'lego') return `Year: ${item.year || 'N/A'}`;
      if (itemType === 'movie') return `Year: ${item.year || 'N/A'} • Condition: ${item.condition || 'N/A'}`;
      if (itemType === 'music') return `Year: ${item.year || 'N/A'} • Format: ${item.format || 'N/A'}`;
      if (itemType === 'book') return `${item.format || 'N/A'}${item.year ? ` • ${item.year}` : ''}`;
      if (itemType === 'board-game') return `${item.playingTime ? `${item.playingTime} min` : 'N/A'} • Year: ${item.year || 'N/A'}`;
      return '';
    },
    getItemDetailUrl: (item) => {
      if (itemType === 'game') return `/item/game/${item._id}`;
      if (itemType === 'console') return `/item/console/${item._id}`;
      if (itemType === 'lego') return `/item/lego/${item._id}`;
      if (itemType === 'movie') return `/item/movie/${item._id}`;
      if (itemType === 'music') return `/item/music/${item._id}`;
      if (itemType === 'book') return `/item/book/${item._id}`;
      if (itemType === 'board-game') return `/item/board-game/${item._id}`;
      return '#';
    },
    getItemDetails: (item) => {
      if (itemType === 'game') {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Played
              </Typography>
              {item.played ? (
                <CheckCircleIcon color="success" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
              {item.completed ? (
                <EmojiEventsIcon sx={{ color: 'gold' }} />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
          </Box>
        );
      }
      if (itemType === 'movie') {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Watched
              </Typography>
              {item.watched ? (
                <CheckCircleIcon color="success" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            {item.director && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Director
                </Typography>
                <Typography variant="body2">{item.director}</Typography>
              </Box>
            )}
          </Box>
        );
      }
      return null;
    },
    getItemNotes: (item) => item.notes,
    showMarketValue: (item) => itemType === 'game' || itemType === 'console' || itemType === 'movie' || itemType === 'music'
  };

  // Merge default render props with custom render props
  const mergedRenderProps = { ...defaultRenderProps, ...renderProps };

  // Handle edit button click with explicit event stopping
  const handleEditClick = (event, item) => {
    event.stopPropagation();
    
    // For movie items, ensure format and condition are properly formatted for the EditDialog
    if (itemType === 'movie' && item) {
      // Create a copy of the item to avoid modifying the original
      const itemCopy = { ...item };
      
      // If format is an object with value/label, extract just the value
      if (itemCopy.format && typeof itemCopy.format === 'object' && itemCopy.format.value) {
        itemCopy.format = itemCopy.format.value;
      }
      
      // If condition is an object with value/label, extract just the value
      if (itemCopy.condition && typeof itemCopy.condition === 'object' && itemCopy.condition.value) {
        itemCopy.condition = itemCopy.condition.value;
      }
      
      // If edition is an object with value/label, extract just the value
      if (itemCopy.edition && typeof itemCopy.edition === 'object' && itemCopy.edition.value) {
        itemCopy.edition = itemCopy.edition.value;
      }
      
      if (onEdit) {
        onEdit(itemCopy);
      }
      return;
    }
    
    if (onEdit) {
      onEdit(item);
    }
  };

  // Handle delete button click with explicit event stopping
  const handleDeleteClick = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onDelete === 'function') {
      onDelete(item);
    }
  };

  return (
    <Grid container spacing={3}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} key={item._id}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            {/* Favourite toggle */}
            {onToggleFavourite && !isWishlist && (
              <Tooltip title={(item.favorite || item.favourite || item.isFavorite) ? 'Remove from Favourites' : 'Add to Favourites'}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onToggleFavourite(item); }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' }
                  }}
                >
                  {(item.favorite || item.favourite || item.isFavorite) ? (
                    <FavoriteIcon sx={{ color: '#ff4081' }} fontSize="small" />
                  ) : (
                    <FavoriteBorderIcon sx={{ color: 'white' }} fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              {mergedRenderProps.getImageUrl(item) ? (
                <img
                  src={getSecureImageUrl(mergedRenderProps.getImageUrl(item))}
                  alt={mergedRenderProps.getItemName(item)}
                  style={{
                    height: 200,
                    maxWidth: '100%',
                    objectFit: 'contain',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(mergedRenderProps.getItemDetailUrl(item), { state: { item, fromWishlist: isWishlist } })}
                  onError={handleImageError}
                />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.200'
                  }}
                >
                  No Image Available
                </Box>
              )}
            </Box>
            <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
              <Typography
                gutterBottom
                variant="h5"
                component="div"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(mergedRenderProps.getItemDetailUrl(item), { state: { item, fromWishlist: isWishlist } })}
              >
                {mergedRenderProps.getItemName(item)}
              </Typography>
              {item.rating && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Rating value={item.rating} readOnly size="small" />
                </Box>
              )}
              <Typography variant="body1" color="text.primary">
                {mergedRenderProps.getItemPrimary(item)}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {mergedRenderProps.getItemSecondary(item)}
              </Typography>
              
              {mergedRenderProps.getItemDetails(item)}
              
              {mergedRenderProps.getItemNotes(item) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Notes: {mergedRenderProps.getItemNotes(item)}
                </Typography>
              )}
              
              {/* eBay Market Price Component */}
              {mergedRenderProps.showMarketValue(item) && (
                <Box sx={{ mt: 3 }}>
                  <EbayPriceValue
                    type={itemType}
                    name={item.name}
                    platform={item.platform}
                    format={item.format}
                    setNum={itemType === 'lego' ? item.setNum : null}
                  />
                </Box>
              )}
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
              <Button 
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={(e) => handleEditClick(e, item)}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<DeleteIcon />}
                onClick={(e) => handleDeleteClick(e, item)}
              >
                Remove
              </Button>
              {isWishlist && onMoveToCollection && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onMoveToCollection(item);
                  }}
                  sx={{ ml: 1 }}
                >
                  Add to Collection
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default FixedDetailedView;
